// Scope type contract helpers.
//
// The B2B permissions backend models the data scope with a `scope_type` field
// inside `scope`. Historically it was mutually exclusive ("internal" OR
// "external"), but it now supports BOTH at once. The backend represents the
// combined case as a comma-separated string, e.g.:
//
//   "scope": { "scope_type": "internal, external", internal: {...}, external: {...} }
//
// These helpers centralize parsing (GET) and serialization (POST) so no consumer
// has to hand-roll split/join logic. Parsing is tolerant: it accepts the CSV
// string the backend currently returns AND a plain array, in case the contract
// evolves. Serialization always emits the CSV string the backend expects today.

export const SCOPE_TYPES = {
    INTERNAL: 'internal',
    EXTERNAL: 'external',
};

const VALID_TYPES = [SCOPE_TYPES.INTERNAL, SCOPE_TYPES.EXTERNAL];

/**
 * Parse the backend `scope_type` into a normalized array of active types.
 *
 * Accepts:
 *   - "internal"               -> ['internal']
 *   - "internal, external"     -> ['internal', 'external']
 *   - "external,internal"      -> ['external', 'internal']  (spaces optional)
 *   - ['internal', 'external'] -> ['internal', 'external']  (already an array)
 *   - null / '' / undefined    -> []
 *
 * Unknown tokens are dropped so a malformed value can never grant an invalid
 * scope. Order and de-duplication are preserved as first-seen.
 */
export const parseScopeType = (scopeType) => {
    if (!scopeType) return [];

    const tokens = Array.isArray(scopeType)
        ? scopeType
        : String(scopeType).split(',');

    const seen = new Set();
    const result = [];
    tokens.forEach((raw) => {
        const token = String(raw).trim().toLowerCase();
        if (VALID_TYPES.includes(token) && !seen.has(token)) {
            seen.add(token);
            result.push(token);
        }
    });
    return result;
};

/**
 * Serialize a list of active scope types into the CSV string the backend expects.
 * Always lists internal before external for a stable, predictable payload.
 *
 *   ['external', 'internal'] -> "internal, external"
 *   ['internal']             -> "internal"
 *   []                       -> ""
 */
export const serializeScopeType = (types = []) => {
    const active = parseScopeType(types); // normalize + validate + de-dupe
    // Emit in canonical order (internal first) regardless of input order.
    return VALID_TYPES.filter((t) => active.includes(t)).join(', ');
};

/** True when the given scope_type (string or array) includes `type`. */
export const scopeIncludes = (scopeType, type) =>
    parseScopeType(scopeType).includes(type);
